<template>
  <BaseCardV2
    :has-btn-action="false"
    :loading="loading"
    has-help
    titre="Mon rendement moyen"
    @on-help="isOpenModalHelp = true"
  >
    <div
      v-if="!loading && !isRendementIncoherent"
      class="mb-2"
    >
      <div class="d-flex justify-center">
        <span
          :class="$vuetify.breakpoint.mobile ? 'chiffre' : 'chiffre-xxl '"
          class=" align-self-center secondary--text"
        >
          {{ valeurNumtiers | roundNumber(precisionRendement) }}
        </span>
        <span
          :class="$vuetify.breakpoint.mobile ? 'texte' : 'chiffre'"
          class="ml-2 align-self-center secondary--text"
        >
          {{ uniteRendement }}
        </span>
        <v-tooltip
          v-if="mentionRendement"
          bottom
        >
          <template v-slot:activator="{ on }">
            <v-icon
              class="align-self-center ml-1"
              color="primary"
              small
              v-on="on"
            >
              mdi-information-outline
            </v-icon>
          </template>
          <span class="texte white--text">{{ mentionRendement }}</span>
        </v-tooltip>
      </div>
      <div
        v-if="evolutionRendement"
        class="mt-2 d-flex justify-center"
      >
        <BaseEvolution
          :evolution-value="evolutionRendement"
          :unite="uniteRendement"
        />
      </div>
      <v-divider class="mx-4 mt-6" />
      <span
        v-if="!isDonneesGroupeInsuffisante && filtresAdherent.regionsAgrial.length && !loading"
        class=" mx-3 mt-5 d-flex texte texte-gris-fonce"
      >
        Où se situe ma donnée par rapport à mon groupe ({{ nbAdherents }} adhérents) ?
      </span>
      <span
        v-if="(isDonneesGroupeInsuffisante && !loading) || filtresAdherent.regionsAgrial.length === 0"
        class=" mx-3 mt-5 d-flex texte texte-gris-fonce"
      >
        <BaseMessageV2 message="Nombre d'adhérents insuffisant pour la comparaison (&lt; 5 adhérent)" />
      </span>
      <!-- graphe rendement -->

      <ComparaisonQuartiles
        v-if="!isDonneesGroupeInsuffisante && filtresAdherent.regionsAgrial.length && !loading"
        class="d-flex justify-center mt-3"
        :data="chartData"
        size="100%"
      />
    </div>
    <div v-if="!loading && isRendementIncoherent">
      <BaseMessage
        class="mx-5 mt-3"
        color="secondary"
        text
        type="info"
      >
        Rendement incohérent : nous ne pouvons pas afficher votre analyse.
      </BaseMessage>
    </div>

    <HelpModal
      v-if="isOpenModalHelp"
      titre="Mon rendement moyen"
      :is-open-modal-help="isOpenModalHelp"
      @close="isOpenModalHelp = false"
    >
      <div slot="baseCardHelpContent">
        <div class="texte text-justify texte-gris-fonce pa-8">
          <p>
            Vous pouvez comparer pour la graine sélectionnée, votre rendement moyen de l'année par rapport aux autres adhérents de votre groupe.

            Visualisez la valeur moyenne de rendements des producteurs considérés, ainsi que le quart supérieur.
          </p>
          <p>
            Le curseur représente votre propre valeur et il est positionné sur ce graphique en fonction de votre situation.

            Les rendements sont issus des données saisies par les adhérents sur la page Mes cultures.
          </p>
        </div>
      </div>
    </HelpModal>
  </BaseCardV2>
</template>
<script>
import BaseCardV2 from '@/components/card/base-card-v2.vue'
import HelpModal from '@/components/modal/help-modal.vue'
import BaseEvolution from '@/components/common/base-evolution.vue'
import numberFilter from '@/filters/number.filter'
import {mapGetters} from 'vuex'
import BaseMessage from '@/components/common/base-message.vue'
import BaseMessageV2 from '@/components/common/base-message-v2.vue'
import ComparaisonQuartiles from '@/components/chart/v2/comparaison-quartiles.vue'
import { convertirRendement, isLinFibre, mentionRendement, precisionRendement, uniteRendement } from '@/utils/rendement'

export default {
  name: 'ComparaisonRendement',
  components: { BaseCardV2, BaseMessage, BaseMessageV2, HelpModal, BaseEvolution, ComparaisonQuartiles },
  props: {
    loading: {
      type: Boolean,
      default: false
    },
    typeCulture: {
      type: Object,
      required: true
    },
    isRendementIncoherent: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      isOpenModalHelp: false,
      erreur: false
    }
  },
  computed: {
    ...mapGetters({
      repartitionRendement: 'rendement/grapheComparaisonRendementGroupe',
      filtresAdherent: 'filtresComparaison/filtresAdherent'
    }),
    isLinFibre () {
      return isLinFibre(this.typeCulture)
    },
    valeurNumtiers () {
      return this.convertir(this.repartitionRendement?.positionPersoDuNumtiers?.valeur)
    },
    isDonneesGroupeInsuffisante () {
      return this.nbAdherents < 5
    },
    evolutionRendement () {
      return this.convertir(this.repartitionRendement.evolutionValeurDuNumtiers)
    },
    nbAdherents () {
      return this.repartitionRendement.nbAdherentsGroupe
    },
    uniteRendement () {
      return uniteRendement(this.typeCulture)
    },
    precisionRendement () {
      return precisionRendement(this.typeCulture)
    },
    mentionRendement () {
      return mentionRendement(this.typeCulture)
    },
    unitePoids () {
      return this.typeCulture.isUniteAfficheeTonneParHectare ? 't' : 'qx'
    },
    chartData () {
      return {
        measure: {
          value: this.repartitionRendement.positionPersoDuNumtiers ? this.formaterRendement(this.repartitionRendement.positionPersoDuNumtiers.valeur, 0) : null,
          legend: this.repartitionRendement.positionPersoDuNumtiers ? this.formaterRendement(this.repartitionRendement.positionPersoDuNumtiers.valeur, this.precisionRendement) + ' ' + this.uniteRendement : null
        },
        min: {
          value: this.repartitionRendement.min ? this.formaterRendement(this.repartitionRendement.min.valeur, 0) : null,
          label: 'Min'
        },
        quartile1: {
          value: this.repartitionRendement.quartile1 ? this.formaterRendement(this.repartitionRendement.quartile1.valeur, 0) : null,
          label: 'Quart inf.'
        },
        mediane: {
          value: this.repartitionRendement.quartile2 ? this.formaterRendement(this.repartitionRendement.quartile2.valeur, 0) : null,
          label: 'Médiane'
        },
        quartile2: {
          value: this.repartitionRendement.quartile3 ? this.formaterRendement(this.repartitionRendement.quartile3.valeur, 0) : null,
          label: 'Quart sup.'
        },
        max: {
          value: this.repartitionRendement.max ? this.formaterRendement(this.repartitionRendement.max.valeur, 0) : null,
          label: 'Max'
        },
        quarters: [
          { color: '#ec6608', legend: this.repartitionRendement.quartile1 ? this.repartitionRendement.quartile1.nombreValeursInferieures + ' producteur(s) ont un rendement moyen compris entre ' + this.formaterRendement(this.repartitionRendement.min.valeur, 0) + ' ' + this.uniteRendement + ' et ' + this.formaterRendement(this.repartitionRendement.quartile1.valeur, 0) + ' ' + this.uniteRendement : null},
          { legend: this.repartitionRendement.quartile2 ? (this.repartitionRendement.quartile2.nombreValeursInferieures - this.repartitionRendement.quartile1.nombreValeursInferieures) + ' producteur(s) ont un rendement moyen compris entre ' + this.formaterRendement(this.repartitionRendement.quartile1.valeur, 0) + ' ' + this.uniteRendement + ' et ' + this.formaterRendement(this.repartitionRendement.quartile2.valeur, 0) + ' ' + this.uniteRendement : null},
          { legend: this.repartitionRendement.quartile3 ? (this.repartitionRendement.quartile3.nombreValeursInferieures - this.repartitionRendement.quartile2.nombreValeursInferieures) + ' producteur(s) ont un rendement moyen compris entre ' + this.formaterRendement(this.repartitionRendement.quartile2.valeur, 0) + ' ' + this.uniteRendement + ' et ' + this.formaterRendement(this.repartitionRendement.quartile3.valeur, 0) + ' ' + this.uniteRendement : null},
          { color: '#01713C', legend: this.repartitionRendement.max ? (this.repartitionRendement.max.nombreValeursInferieures - this.repartitionRendement.quartile3.nombreValeursInferieures + 1) + ' producteur(s) ont un rendement moyen compris entre ' + this.formaterRendement(this.repartitionRendement.quartile3.valeur, 0) + ' ' + this.uniteRendement + ' et ' + this.formaterRendement(this.repartitionRendement.max.valeur, 0) + ' ' + this.uniteRendement : null}
        ],
        unit: this.unitePoids,
        icon: 'mdi-account'
      }
    },
  },
  methods: {
    convertir (valeurEnQuintauxParHectare) {
      return convertirRendement(valeurEnQuintauxParHectare, this.typeCulture)
    },
    formaterRendement (valeurEnQuintauxParHectare, precision) {
      return numberFilter.formatNumber(this.convertir(valeurEnQuintauxParHectare), precision)
    }
  }
}
</script>
